<template>
	<ion-card class="flex ion-align-items-center px-1 py-1" :class="{'completed': thisTask.completed, 'active': isActive, 'border border-[#c5000f]': isWarning}">
		<ion-progress-bar v-if="pressStart || isActive" class="w-100 absolute tracking-indicator" :value="holdPercentage" />
		<ion-checkbox v-if="isCompletable && !thisTask.unassigned && !treatAsUnassigned" @ionChange="toggleCompleted" :checked="thisTask.completed" class="mr-1" />

		<div class="info" v-touch:hold="taskHold" v-touch:release="taskRelease" v-touch:drag="taskSlide">
			<h5 :class="{'font-bold': showProject}">{{ thisTask.name }}</h5>
			<h5 v-if="showProject">Project: {{ projectName ?? project?.name }}</h5>

			<h6 v-if="thisTask.unassigned">Unassigned</h6>
			<div v-else-if="isActive" class="time-tracking-wrapper w-fit flex items-center mt-0.5">
				<ion-icon :icon="$icons.timerOutline" />
				<p>{{ Date.time(totalTime).format() }}</p>
			</div>
			<h6 v-else-if="totalTime">Tracked time: {{ Date.time(totalTime).format("k'h' mm'm'") }}</h6>
		</div>
		<ion-icon v-if="isWarning" :icon="$icons.alert" class="size-xxl text-danger"/>
		<ion-progress-bar v-if="thisTask.plannedTime" :value="progress" :color="progress > 1 ? 'warning' : 'success'" class="w-100 absolute progress-indicator"/>
	</ion-card>
</template>

<script>
import ModalTask from './components/modals/modal-task.vue'

import { mapGetters, mapActions } from 'vuex'

import axios from '@/plugins/w/axios/models/axios'
const api = {
	updateTask: (taskId, newData) => axios.patch_auth_data(`/tasks/${taskId}`, newData)
}

export default {
	props: {
		task: {
			required: true,
			type: Object
		},

		project: {
			type: Object
		},

		projectName: {
			type: String
		},

		hasModal: {
			type: Boolean,
			default: true
		},

		startTrackingAction: {
			type: Function
		},

		isCompletable: {
			type: Boolean,
			default: true
		},

		showProject: {
			type: Boolean,
			default: false
		},

		treatAsUnassigned: {
			type: Boolean,
			default: false
		}
	},
	
	data() {
		return {
			thisTask: this.task,
			pressStart: null,
			holdPercentage: 0,

			modalData: []
		}
	},

	created() {
		this._updateTaskTracking()

		this.$eventBus.on('updatedCategory', this.updatedCategory)
	},

	unmounted() {
		this.$eventBus.off('updatedCategory', this.updatedCategory)
	},

	emits: ['clicked'],

	computed: {
		...mapGetters('projectsStore', ['getActiveTask', 'getCurrentTrackedTime']),

		totalTime() {
			return this.isActive ? this.thisTask.totalTime + this.getCurrentTrackedTime : this.thisTask.totalTime
		},

		progress() {
			return this.totalTime / this.thisTask.plannedTime
		},

		isActive() {
			return this.getActiveTask?._id == this.thisTask._id
		},

		isWarning() {
			const isOvertracked = this.modalData?.times?.some(time => time.duration >= 57600)
			return isOvertracked || this.thisTask.emptyCategoryAlert
		}
	},

	methods: {
		...mapActions('projectsStore', ['startTracking', 'stopTracking', 'stopTrackingAction']),

		async _updateTaskTracking() {
			this.holdPercentage = this.isActive ? 1 : 0
		},

		async toggleCompleted(ev) {
			try {
				if (ev.target.checked && this.isActive) {
					await this.stopTrackingAction()
				}

				const updatedTask = await api.updateTask(this.thisTask._id, { completed: ev.target.checked })
				this.$eventBus.emit('updateTask', updatedTask)
			} catch (err) {
				console.error(err)
			}
		},

		async openModalTask() {
			const modal = await this.$modals.open(ModalTask, {
				task: this.thisTask,
				project: this.project,
				isUnassigned: this.thisTask.unassigned || this.treatAsUnassigned
			})
			modal.onDidDismiss().then(({ data }) => this.modalData = data)
		},

		// ----- Task Hold Actions ------

		taskHold() {
			this.pressStart = new Date()
			if (this.thisTask.completed) return
			
			setTimeout(() => this.holdPercentage = this.isActive ? 0 : 1, 10)

			this.holdTimer = setTimeout(async () => {
				if (this.isActive) {
					await this.stopTracking()
				} 
				else {
					await this.startTrackingAction?.(this.thisTask) || await this.startTracking(this.thisTask)
				}

				this._updateTaskTracking()
				this.holdTimer = null
			}, 420)
		},

		taskSlide(ev) {
			if (Math.abs(ev.movementX + ev.movementY) > 2) {
				this.taskRelease()
			}
		},

		updatedCategory(ev) {
			if (this.thisTask._id != ev?.task._id)
				return

			this.thisTask.category = ev.newValue
			this.thisTask.emptyCategoryAlert = ev.task.emptyCategoryAlert
		},

		async taskRelease(ev) {
			const clicked = ev?.cancelable && new Date() - this.pressStart < 180
			if (clicked)
			{
				this.$emit('clicked', ev)

				if (this.hasModal)
					await this.openModalTask()
			}

			clearTimeout(this.holdTimer)
			this.holdTimer = null

			this._updateTaskTracking()
		}
	},

	watch: {
		getActiveTask() {
			this._updateTaskTracking()
		}
	}
}
</script>

<style lang="sass" scoped>
*
	user-select: none !important

ion-card
	border: 1px solid var(--ion-color-medium-tint)
	border-radius: 6px
	margin: 0
	margin-bottom: 10px
	box-shadow: none
	cursor: pointer
	position: relative

	&.active
		padding-bottom: 0
		padding-top: 0

	.tracking-indicator
		top: 0
		left: 0 
		height: 4px
		--background: transparent
		border-radius: 6px 6px 0 0

		&::part(progress)
			transition: ease-in calc(420ms + 2ms) !important

	.progress-indicator
		bottom: 0
		left: 0
	
	.info
		flex: 1 1 100%

		h5
			color: var(--ion-text-color)
			font-weight: 400
			margin: 0
			word-break: break-word

		h6
			margin: 5px 0 0
			font-weight: 400

.completed
	opacity: 0.5

	ion-checkbox
		--border-color: 1px solid var(--ion-color-medium-tint)
		--border-color-checked: 1px solid var(--ion-color-medium-tint)
		--checkmark-color: var(--ion-color-medium-tint)
		--background-checked: transparent

.time-tracking-wrapper
	padding: 2px 5px

	border: 1px solid var(--ion-color-medium-tint)
	border-radius: 4px

	ion-icon
		margin-right: 5px

	p
		margin: 0
		width: fit-content

</style>